import { useMemo } from 'react';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { useGetCoverBackgroundsQuery, useGetCoverEditorQuery } from '../../../../../redux/api/fymAPI';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';

/** One picture that a Spotify header can use: its uuid, the thumbnail and the full picture. */
export interface HeaderPicture {
  uuid: string;
  thumbUrl: string;
  url: string;
}

export interface HeaderPictures {
  /** The published bank pictures, the pictures of the song theme first. */
  bank: HeaderPicture[];
  /** The ready AI pictures of the song (the cover editor), newest first. */
  ai: HeaderPicture[];
  isLoading: boolean;
}

const NO_PICTURES: HeaderPicture[] = [];

/**
 * The pictures of step 3 of a Spotify header (design spotify-header a2): the cover bank
 * (`GET /public/cover-backgrounds`) and the ready AI pictures of the song (`GET .../cover-editor/:uuid`).
 * No new AI call: a header only uses a picture that the cover editor made.
 */
export function useHeaderPictures(input: {
  accountId: number;
  /** The song of the AI pictures. Null (the artist Profile with no release yet): no AI pictures. */
  catalogItemUuid: string | null;
  themeId: ReleaseThemeId;
}): HeaderPictures {
  const { accountId, catalogItemUuid, themeId } = input;
  const backgrounds = useGetCoverBackgroundsQuery();
  const editor = useGetCoverEditorQuery(
    { accountId, catalogItemUuid: catalogItemUuid ?? '' },
    { skip: catalogItemUuid === null }
  );
  const bank = useMemo(() => {
    const list = backgrounds.data?.backgrounds ?? [];
    const fits = (themeIds: readonly string[]) => (themeIds.includes(themeId) ? 0 : 1);
    return [...list]
      .sort((a, b) => fits(a.themeIds) - fits(b.themeIds))
      .map((picture) => ({
        uuid: picture.uuid,
        thumbUrl: getFullAPIFileURL(picture.thumbUrl),
        url: getFullAPIFileURL(picture.url),
      }));
  }, [backgrounds.data, themeId]);
  const ai = useMemo(
    () =>
      (editor.data?.pictures ?? []).flatMap((picture) =>
        picture.status === 'ready' && picture.url !== null
          ? [
              {
                uuid: picture.uuid,
                thumbUrl: getFullAPIFileURL(picture.thumbUrl ?? picture.url),
                url: getFullAPIFileURL(picture.url),
              },
            ]
          : []
      ),
    [editor.data]
  );
  const aiPictures = catalogItemUuid === null ? NO_PICTURES : ai;
  return { bank, ai: aiPictures, isLoading: backgrounds.isLoading || (catalogItemUuid !== null && editor.isLoading) };
}
